import { useState } from 'react';
import { api, fmt, fmtDate } from '../api.ts';

export interface DateResult {
  jobNumber: string; previous: string | null; deliveryDate: string | null; ownDeliveryDate: boolean;
}

const day = (v: string | null | undefined) => (v ? String(v).slice(0, 10) : '');

export default function DeliveryDateDialog({ job, onClose, onDone }: {
  job: {
    job_id: number; job_number: string; customer_name: string; item_name: string; ordered_quantity: string;
    delivery_date: string | null; line_delivery_date?: string | null; own_delivery_date?: boolean;
  };
  onClose: () => void;
  onDone: (r: DateResult, undo: string | null) => void;
}) {
  const current = day(job.delivery_date);
  const line = day(job.line_delivery_date);
  const [date, setDate] = useState(current);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');

  const sameAsLine = date === line;
  const changed = date !== current || (sameAsLine && job.own_delivery_date);

  async function save(value: string | null) {
    setBusy(true); setError('');
    try {
      const r = await api.put<DateResult>(`/api/jobs/${job.job_id}/delivery-date`, { deliveryDate: value });
      onDone(r, job.own_delivery_date ? current : null);
      onClose();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Could not save the date');
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="backdrop" onClick={() => !busy && onClose()}>
      <div className="modal" style={{ width: 400 }} role="dialog" aria-label={`Delivery date for ${job.job_number}`}
           onClick={(e) => e.stopPropagation()}>
        <div className="modal-head">
          <h3>Delivery date for {job.job_number}</h3>
          <p>{job.customer_name} · {job.item_name} · {fmt(job.ordered_quantity)}</p>
        </div>
        <div className="modal-body">
          {error && <div className="error" style={{ marginBottom: 12 }}>{error}</div>}
          <div className="field" style={{ marginBottom: 0 }}>
            <label htmlFor="part-date">Delivery date</label>
            <input id="part-date" type="date" autoFocus value={date} onChange={(e) => setDate(e.target.value)} />
            <div className="hint">
              {line ? `Order line: ${fmtDate(line)}` : 'The order line has no date'}
              {line && !sameAsLine && (
                <>{' · '}<button className="linkish" onClick={() => setDate(line)}>Use it</button></>
              )}
            </div>
          </div>
          {changed && date && (
            <ul className="undo-list" style={{ marginTop: 16 }}>
              <li>{current ? `${fmtDate(current)} → ${fmtDate(date)}` : fmtDate(date)}</li>
              {sameAsLine && <li>Follows the order line again</li>}
            </ul>
          )}
        </div>
        <div className="modal-foot">
          <button className="btn" onClick={onClose} disabled={busy}>Cancel</button>
          <button className="btn primary" disabled={busy || !changed || !date}
                  onClick={() => void save(sameAsLine ? null : date)}>
            {busy ? 'Saving…' : 'Save date'}
          </button>
        </div>
      </div>
    </div>
  );
}
